/* API del flujo "Nuevo gasto" (QA-007): documento autónomo leído antes de elegir informe; informes elegibles; creación
 * idempotente de informe y gasto (misma Idempotency-Key entre reintentos → una sola ejecución en backend). */
import { useQuery } from '@tanstack/react-query';
import { api, get } from '@/shared/api/client';
import { type Document, uploadWithProgress } from '@/features/documents/api';
import type { Expense, ExpenseIn, Report, ReportRow } from '@/features/reports/api';

export const EDITABLE_STATUSES = ['BORRADOR', 'OBSERVADO_COORDINADOR', 'OBSERVADO_FINANZAS', 'REABIERTO'] as const;
import { compressImage } from '@/features/documents/api';

export type DraftContext = { category_code?: string; subtype_code?: string; report_id?: number };

/** Sube la foto/archivo sin gasto ni informe; `uploadId` estable entre reintentos (no duplica documento ni lectura). */
export async function uploadStandaloneDocument(file: File, uploadId: string, draft: DraftContext, onProgress: (p: number) => void = () => undefined): Promise<Document> {
  const { blob, name, compressed } = await compressImage(file);
  const form = new FormData();
  form.append('file', blob, name);
  form.append('client_compressed', String(compressed));
  form.append('client_upload_id', uploadId);
  if (draft.category_code) form.append('category_code', draft.category_code);
  if (draft.subtype_code) form.append('subtype_code', draft.subtype_code);
  if (draft.report_id) form.append('report_id', String(draft.report_id));
  return uploadWithProgress<Document>('/documents', form, onProgress);
}

/** Informes donde el usuario puede agregar gastos (editables), más recientes primero. */
export const useEligibleReports = (enabled = true) => useQuery({ queryKey: ['reports', 'mine', 'eligible'], queryFn: async () => (await get<ReportRow[]>(`/reports/mine?status=${EDITABLE_STATUSES.join(',')}`)).slice().sort((a, b) => b.id - a.id), enabled });

export const createReportIdempotent = (body: { title: string; fund_ids: number[]; reviewer_user_id: number | null }, key: string) => api<Report>('/reports', { method: 'POST', body, idempotencyKey: key });
export const addExpenseIdempotent = (reportId: number, body: ExpenseIn & { document_public_id?: string }, key: string) => api<Expense>(`/reports/${reportId}/expenses`, { method: 'POST', body, idempotencyKey: key });

export const newKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
